/**
 * The permission vocabulary and the system roles.
 *
 * Lives here rather than in `@powwow/domain` because every client needs it: the management app
 * decides what to render from the permissions a user holds, and importing the domain package into
 * a browser bundle to read a list of strings would drag the persistence layer along with it. The
 * *enforcement* stays in the domain and on the server; this is only the vocabulary.
 *
 * Two separations here are deliberate and come straight from the specification:
 *
 * - **Approving a result and approving a payout are different permissions.** The person who
 *   confirms a standing is not necessarily the person who authorises money leaving a table.
 * - **Registering into a closed window is its own permission**, not an implication of being able
 *   to register. It is used routinely rather than exceptionally (D20), but it is still the thing
 *   that lets someone bypass a deadline, and it should be grantable to the desk lead without
 *   being grantable to every volunteer on the desk.
 */

export const PERMISSIONS = {
  // Community administration
  "community.manage": "Change community settings, branding and terminology",
  "community.members.manage": "Invite, remove and change the roles of members",
  "community.audit.read": "Read the audit log",

  // Events and configuration
  "event.read": "See events, including drafts",
  "event.manage": "Create and edit events, sessions and the event roster",
  "event.publish": "Publish an event to the public site",
  "event.clone": "Duplicate an event, a form or a scoring configuration",
  "competition.manage": "Create and edit competitions, categories and scoring configuration",

  // Registration
  "registration.read": "See participants and registrations",
  "registration.create": "Register a participant",
  "registration.manage": "Edit and cancel registrations",
  /** The override of D14 — recorded with actor, timestamp and reason every time it is used. */
  "registration.override_window": "Register into a closed registration window",
  /** Registration *and* check-in: one table, one rule, and this one permission moves either — so a
   *  label naming only registration understated what granting it hands over (D66, D84). */
  "registration.window.manage": "Open and close registration and check-in windows during an event",
  /** Its own permission because a merge rewrites history across events — routine data entry is not. */
  "participant.merge": "Merge duplicate participant profiles",
  "team.manage": "Create and manage teams and their members",

  // Check-in
  "checkin.record": "Check participants in",
  "checkin.reverse": "Undo a check-in after the fact",

  // Judging
  "judge.assign": "Assign judges to sessions and categories, and issue access codes",
  "judge.score": "Enter scores for an assigned category",
  "judge.override_score": "Change a locked score",

  // Results
  "result.read": "See computed results before publication",
  "result.tabulate": "Compute and recompute results",
  "result.override_rank": "Override a computed rank",
  "result.approve": "Approve a result set",
  "result.publish": "Publish results to the public site",

  // Money — deliberately separate from result approval
  "prize.manage": "Create and edit prize rules",
  "payout.read": "See calculated payouts",
  "payout.calculate": "Calculate payouts",
  "payout.approve": "Approve payouts for payment",
  "payout.pay": "Mark payouts as paid",
  "payout.void": "Void a payout",

  // Forms, surveys, exports
  "form.manage": "Build and publish forms",
  "survey.manage": "Create surveys and read responses",
  "export.create": "Export data",
  /**
   * Printing the day's sheets, which is not the same act as exporting the data.
   *
   * `export.create` is the answer to "who took a copy of the participant list", and it is held by
   * organisers, treasurers and analysts. Printing a blank registration sheet is not that: it is the
   * paper the desk falls back to when the signal goes, and until this existed the two people who
   * need it most — the registrar and the gate worker — could not produce a single sheet without
   * being handed the whole export surface, contact-details switch and all.
   *
   * So this is the narrow half: the operational sheets in `PRINTABLE_SHEETS`, never with contact
   * details, still written to `export_jobs` with a name against it. `export.create` includes it.
   */
  "export.print": "Print blank sheets, rosters and signature sheets",

  /**
   * Give someone a role, within whatever this grant is scoped to.
   *
   * The scope is the whole design. Held at community scope it assigns anywhere; held on one event
   * it assigns within that event and its competitions; held on one competition it assigns within
   * that competition. So delegation walks down the tree and never sideways — which is what lets a
   * community hand an event to somebody without handing them the community (D42).
   *
   * Separate from `community.members.manage`, which is membership itself: inviting, deactivating,
   * and the role a person holds across the whole community.
   */
  "roles.assign": "Assign roles to people, within what you are responsible for",

  // Communication and insight
  "announcement.manage": "Publish updates to the public event page",
  "analytics.read": "See analytics, charts and reports",
  "email.read": "See the email delivery history",
} as const;

export type Permission = keyof typeof PERMISSIONS;

export const ALL_PERMISSIONS = Object.keys(PERMISSIONS) as Permission[];

/**
 * The roles every community starts with.
 *
 * These are a starting point, not a fixed set — `roles.community_id` exists so a community can
 * define its own, and `permission_grants` scope them to a session or a category. The pilot's shape
 * is seed data, not schema.
 */
export const SYSTEM_ROLES = {
  owner: {
    name: "Owner",
    description: "Full control of the community, including members and money",
    permissions: ALL_PERMISSIONS,
  },
  /**
   * The person who runs the pow wow, with an administrator's reach.
   *
   * The narrow reading of "organiser" — sets things up, hands the money to somebody else — assumes
   * a staffed organisation. A community running one event a year does not have a separate treasurer
   * and a separate results approver and a separate administrator; it has one or two people doing
   * all of it on a weekend, and a role that cannot pay a dancer is a role that produces a phone
   * call to whoever holds the owner account, in a field, with no signal.
   *
   * So this is broad on purpose: settings, members, results and money. The narrower roles below
   * still exist, and a community with the staff to separate these duties should use them — that is
   * the point of having them. This is the default for the person in charge, not a ceiling.
   *
   * **Two are deliberately withheld**, and they are the same line in both cases: an organiser may
   * not enter a judge's score (`judge.score`) and may not change one after it is locked
   * (`judge.override_score`). Everything else here is authority over the event; those two are
   * authority over what a judge saw and recorded, which belongs to the judge and to whoever the
   * community appoints above them. An organiser who is also judging gets a judge grant scoped to
   * their category — which is exactly what `permission_grants` is for (D15).
   */
  organiser: {
    name: "Organiser",
    description: "Sets up and runs events, with administrative access to the community",
    permissions: [
      "community.manage",
      "community.members.manage",
      "community.audit.read",
      // Community scope, so this reaches every event and every competition below them.
      "roles.assign",
      "event.read",
      "event.manage",
      "event.publish",
      "event.clone",
      "competition.manage",
      "registration.read",
      "registration.create",
      "registration.manage",
      "registration.override_window",
      "registration.window.manage",
      "checkin.record",
      "checkin.reverse",
      "judge.assign",
      "result.read",
      "result.tabulate",
      "result.override_rank",
      "result.approve",
      "result.publish",
      "prize.manage",
      "payout.read",
      "payout.calculate",
      "payout.approve",
      "payout.pay",
      "payout.void",
      "form.manage",
      "survey.manage",
      "export.create",
      "announcement.manage",
      "analytics.read",
      "team.manage",
      "participant.merge",
      "email.read",
    ],
  },
  /**
   * One event, run end to end — but only the one they were given.
   *
   * The organiser role above is community-wide by nature: settings, members, every event. This is
   * the same job bounded to a single event, which is what a community needs when a different person
   * runs the winter gathering from the one who runs the summer pow wow, or when last year's
   * organiser is helping with this year's without being handed the community again.
   *
   * Nothing here is community-wide, which is the point — no `community.manage`, no
   * `community.members.manage`. What it does carry is `roles.assign`, so an event manager can staff
   * their own event: assign a registrar to one competition, a check-in worker to a session, a judge
   * to a category. Granted at event scope, that authority reaches the event's competitions and no
   * further (D42).
   */
  event_manager: {
    name: "Event Manager",
    description: "Runs one event end to end, including staffing it",
    permissions: [
      "event.read",
      "event.manage",
      "event.publish",
      "event.clone",
      "competition.manage",
      "registration.read",
      "registration.create",
      "registration.manage",
      "registration.override_window",
      "registration.window.manage",
      "checkin.record",
      "checkin.reverse",
      "judge.assign",
      "result.read",
      "result.tabulate",
      "result.override_rank",
      "result.approve",
      "result.publish",
      "prize.manage",
      "payout.read",
      "payout.calculate",
      "payout.approve",
      "payout.pay",
      "payout.void",
      "form.manage",
      "survey.manage",
      "export.create",
      "announcement.manage",
      "analytics.read",
      "team.manage",
      "roles.assign",
    ],
  },
  /**
   * One competition — the Dance contest, or the Drum contest, not both.
   *
   * This is the role that makes the specific complaint answerable: a registrar for Dance should not
   * be taking entries at the Drum contest. Granted at competition scope, every check below is
   * evaluated against the competition the entry names, so the same person at the same desk is
   * permitted for one and refused for the other.
   *
   * No `roles.assign`: staffing is the event manager's, one level up. A competition lead who needs
   * another pair of hands asks for them rather than minting them, which keeps the list of who may
   * hand out authority short enough to read.
   */
  competition_lead: {
    name: "Competition Lead",
    description: "Runs one competition — its entries, its panel and its results",
    permissions: [
      "event.read",
      "competition.manage",
      "registration.read",
      "registration.create",
      "registration.manage",
      "checkin.record",
      "checkin.reverse",
      "judge.assign",
      "result.read",
      "result.tabulate",
      "team.manage",
      "export.print",
    ],
  },
  registrar: {
    name: "Registrar",
    description: "Runs the registration desk",
    permissions: [
      "event.read",
      "registration.read",
      "registration.create",
      "registration.manage",
      "registration.override_window",
      "checkin.record",
      "checkin.reverse",
      "team.manage",
      "export.print",
    ],
  },
  check_in: {
    name: "Check-in",
    description: "Checks participants in at the gate",
    permissions: ["event.read", "registration.read", "checkin.record", "export.print"],
  },
  arena_director: {
    name: "Arena Director",
    description: "Runs the arena and the running order",
    permissions: [
      "event.read",
      "registration.read",
      "judge.assign",
      "result.read",
      "checkin.record",
      "export.print",
    ],
  },
  judge: {
    name: "Judge",
    description: "Scores an assigned category",
    // Only `judge.score`, and only within the session and category the grant is scoped to. A judge
    // signing in with an access code can reach nothing else in the system (ux.md §3.6).
    permissions: ["judge.score"],
  },
  tabulator: {
    name: "Tabulator",
    description: "Computes results and prepares them for approval",
    permissions: [
      "event.read",
      "registration.read",
      "result.read",
      "result.tabulate",
      "result.override_rank",
      "export.print",
    ],
  },
  results_approver: {
    name: "Results Approver",
    description: "Approves and publishes results",
    permissions: ["event.read", "result.read", "result.approve", "result.publish"],
  },
  treasurer: {
    name: "Treasurer",
    description: "Calculates, approves and pays out prize money",
    permissions: [
      "event.read",
      "registration.read",
      "result.read",
      "prize.manage",
      "payout.read",
      "payout.calculate",
      "payout.approve",
      "payout.pay",
      "payout.void",
      "export.create",
    ],
  },
  analyst: {
    name: "Analyst",
    description: "Reads reports and exports data, without touching event settings",
    permissions: [
      "event.read",
      "registration.read",
      "result.read",
      "analytics.read",
      "export.create",
    ],
  },
  viewer: {
    name: "Viewer",
    description: "Reads without changing anything",
    permissions: ["event.read", "registration.read", "result.read", "payout.read"],
  },
} as const satisfies Record<
  string,
  { name: string; description: string; permissions: readonly Permission[] }
>;

export type SystemRoleKey = keyof typeof SYSTEM_ROLES;

/** The scope a grant can be narrowed to. `session` is here because a judge's authority is
 *  bounded by the day, not the event (D15). */
export const GRANT_SCOPES = [
  "community",
  "event",
  "session",
  "competition",
  "category",
  "phase",
] as const;

export type GrantScope = (typeof GRANT_SCOPES)[number];

/**
 * Scopes ordered widest first. The order *is* the containment rule: a scope contains every scope
 * after it, which is how "an event manager may assign within their event, but not above it" is
 * decided without a tree walk.
 */
export const SCOPE_ORDER: readonly GrantScope[] = [
  "community",
  "event",
  "session",
  "competition",
  "category",
  "phase",
];

/** True when `outer` is the same as, or wider than, `inner`. */
export function scopeContains(outer: GrantScope, inner: GrantScope): boolean {
  return SCOPE_ORDER.indexOf(outer) <= SCOPE_ORDER.indexOf(inner);
}

/**
 * Where each role is worth granting.
 *
 * Not a security control — `isPermitted` is, and it does not consult this. This is the answer to
 * "which roles make sense here", so the screen offers a short, sensible list instead of every role
 * at every level and a person choosing something that silently does nothing.
 *
 * The judgements behind the entries:
 *
 * - **Owner and organiser are community-only.** A "community administrator for one event" is a
 *   contradiction; the role bounded to an event is `event_manager`, which exists for exactly that.
 * - **`event_manager` is event-only.** At community scope it would be an organiser with a longer
 *   name; below an event it would be managing an event it cannot see the whole of.
 * - **`competition_lead` is competition-only**, for the same reason in the other direction.
 * - **The operational roles — registrar, check-in, arena director, tabulator — go anywhere from the
 *   community down to a competition**, because that is the real range: a community's registrar all
 *   year, one event's registrar for a weekend, or the Dance contest's registrar while somebody else
 *   takes Drum.
 * - **A judge is never community-wide.** Their authority is bounded by the day and the category
 *   they were assigned (D15), and a community-scoped judge would be one who may score anything.
 */
export const ROLE_SCOPES: Record<SystemRoleKey, readonly GrantScope[]> = {
  owner: ["community"],
  organiser: ["community"],
  event_manager: ["event"],
  competition_lead: ["competition"],
  registrar: ["community", "event", "competition"],
  check_in: ["community", "event", "session", "competition"],
  arena_director: ["community", "event", "session"],
  judge: ["session", "competition", "category"],
  tabulator: ["community", "event", "competition", "category"],
  results_approver: ["community", "event", "competition"],
  treasurer: ["community", "event"],
  analyst: ["community", "event"],
  viewer: ["community", "event", "competition"],
};

/** Whether this role is worth offering at this scope. */
export function roleFitsScope(role: SystemRoleKey, scope: GrantScope): boolean {
  return ROLE_SCOPES[role].includes(scope);
}
